<template>
    <div class="nonpathological-section-modern">
        <div class="section-header-modern">
            <div class="header-title-modern">
                <i class="fas fa-user-check"></i>
                <h4>Antecedentes personales no patológicos</h4>
            </div>
        </div>

        <!-- Botones para agregar campos -->
        <div v-if="inactiveFields.length > 0" class="add-fields-section">
            <button
v-for="field in inactiveFields" :key="field.name" class="add-field-btn"
                :title="'Agregar ' + field.label" @click="addField(field.name)">
                <i class="fas fa-plus"></i>
                <span>{{ field.label }}</span>
            </button>
        </div>

        <!-- Campos activos -->
        <div v-if="activeFields.length > 0" class="fields-grid-compact">
            <div v-for="field in activeFields" :key="field.name" class="field-group-compact">
                <div class="field-header">
                    <label class="form-label-modern">
                        <span class="label-content-modern">
                            <i :class="field.icon"></i>
                            <span>{{ field.label }}</span>
                        </span>
                    </label>
                    <button class="close-field-btn" title="Cerrar campo" @click="removeField(field.name)">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <MenuSelectedFull
v-if="field.type === 'menu'" v-model="field.model" :label="''"
                    :category="field.category" :apiurl="field.apiurl" :can-edit-and-add="field.canEditAdd"
                    :local-data="field.localData" />

                <ToggleButton
v-else-if="field.type === 'toggle'" v-model="field.model" :text-value="field.textModel"
                    :options="field.options" :input-condition="field.inputCondition"
                    @update:text-value="updateTextValue(field.name, $event)" />
            </div>
        </div>

        <div class="last-updated-wrapper">
            <LastUpdated :updated-at="updated_at" />
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import api from '@/services/api';
import ToggleButton from "@/components/ToggleButton.vue";
import MenuSelectedFull from "@/components/MenuSelectedFull.vue";
import LastUpdated from "@/components/LastUpdated.vue";
import { religionGroups, countryGroups, schoolGroups, maritalGroups, bloodGroups } from '@/data/menuvalues';
import { saveToLocalStorage } from '@/store/Expedientes/pacientes';
import debounce from 'lodash.debounce';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    },
    data: {
        type: Object,
        default: () => ({})
    }
});

const updated_at = ref('');

// Definir campos con su configuración
const fields = ref([
    { name: 'place_of_origin', label: 'Lugar de Origen', type: 'menu', icon: 'fas fa-map-marker-alt', category: '1', apiurl: 'menuvalues', canEditAdd: true, model: 0, isActive: false },
    { name: 'marital_status', label: 'Estado Civil', type: 'menu', icon: 'fas fa-heart', category: '2', localData: maritalGroups, canEditAdd: false, model: 0, isActive: false },
    { name: 'religion', label: 'Religión', type: 'menu', icon: 'fas fa-pray', category: '3', localData: religionGroups, canEditAdd: false, model: 0, isActive: false },
    { name: 'education_level', label: 'Escolaridad', type: 'menu', icon: 'fas fa-graduation-cap', category: '4', localData: schoolGroups, canEditAdd: false, model: 0, isActive: false },
    { name: 'nationality', label: 'Nacionalidad', type: 'menu', icon: 'fas fa-flag', category: '5', localData: countryGroups, canEditAdd: false, model: 0, isActive: false },
    { name: 'place_of_residence', label: 'Lugar de residencia', type: 'menu', icon: 'fas fa-home', category: '6', apiurl: 'menuvalues', canEditAdd: true, model: 0, isActive: false },
    { name: 'occupation', label: 'Ocupación', type: 'menu', icon: 'fas fa-briefcase', category: '7', apiurl: 'menuvalues', canEditAdd: true, model: 0, isActive: false },
    { name: 'blood_group', label: 'Grupo Sanguíneo', type: 'menu', icon: 'fas fa-tint', category: '8', localData: bloodGroups, canEditAdd: false, model: 0, isActive: false },
    { name: 'adequate_hygiene', label: 'Higiene Adecuada', type: 'toggle', icon: 'fas fa-hands-wash', model: 2, options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }], inputCondition: [], isActive: false },
    { name: 'adequate_room', label: 'Habitación Adecuada', type: 'toggle', icon: 'fas fa-bed', model: 2, options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }], inputCondition: [], isActive: false },
    { name: 'adequate_nutrition', label: 'Alimentación Adecuada', type: 'toggle', icon: 'fas fa-utensils', model: 2, textModel: '', options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }, { id: 3, label: 'Opcional' }], inputCondition: [3], isActive: false },
    { name: 'immunizations', label: 'Inmunizaciones', type: 'toggle', icon: 'fas fa-syringe', model: 2, options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }], inputCondition: [], isActive: false },
    { name: 'alcoholism', label: 'Alcoholismo', type: 'toggle', icon: 'fas fa-wine-bottle', model: 2, textModel: '', options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }, { id: 3, label: 'Opcional' }], inputCondition: [1, 3], isActive: false },
    { name: 'smoking', label: 'Tabaquismo', type: 'toggle', icon: 'fas fa-smoking', model: 2, textModel: '', options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }, { id: 3, label: 'Opcional' }], inputCondition: [1, 3], isActive: false },
    { name: 'drug_use', label: 'Drogas', type: 'toggle', icon: 'fas fa-pills', model: 2, textModel: '', options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }, { id: 3, label: 'Opcional' }], inputCondition: [1, 3], isActive: false },
    { name: 'physical_activity', label: 'Actividad física', type: 'toggle', icon: 'fas fa-running', model: 2, textModel: '', options: [{ id: 1, label: 'Sí' }, { id: 2, label: 'No' }, { id: 3, label: 'Opcional' }], inputCondition: [1, 3], isActive: false }
]);

// Computed para campos activos e inactivos
const activeFields = computed(() => fields.value.filter(f => f.isActive));
const inactiveFields = computed(() => fields.value.filter(f => !f.isActive));

// Funciones para agregar/remover campos
const addField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) field.isActive = true;
};

const removeField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) field.isActive = false;
};

const updateTextValue = (name, value) => {
    const field = fields.value.find(f => f.name === name);
    if (field) field.textModel = value;
};

const saveNonPathologicalData = async () => {
    const nonPathologicalData = { non_pathological: {} };

    fields.value.forEach(field => {
        if (field.type === 'toggle' && field.textModel !== undefined) {
            nonPathologicalData.non_pathological[`${field.name}_text`] = field.textModel || '';
        }
        nonPathologicalData.non_pathological[field.name] = field.model;
    });

    try {
        const response = await api.put(`/pacientes/${props.patientRecord.id}/non-pathological`, nonPathologicalData);
        const updatedAt = new Date(response.data.data.non_pathological.updated_at);
        updated_at.value = updatedAt.toString();
        saveToLocalStorage(`patient_${props.patientRecord.id}_non_pathological`, nonPathologicalData.non_pathological);
    } catch (error) {
        console.error('Error al guardar los datos no patológicos:', error);
    }
};

const debouncedSaveNonPathologicalData = debounce(saveNonPathologicalData, 1000);

// Watch para cambios en los campos
watch(fields, () => {
    debouncedSaveNonPathologicalData();
}, { deep: true });

watch(() => props.data, (newData) => {
    if (newData) {
        fields.value.forEach(field => {
            const value = newData[field.name];
            if (value !== undefined && value !== null) {
                field.model = value;
                // Activar campo si tiene valor distinto al por defecto
                if (field.type === 'menu' && value !== 0) {
                    field.isActive = true;
                } else if (field.type === 'toggle' && value !== 2) {
                    field.isActive = true;
                }
            }

            // Cargar texto adicional para toggles con texto
            if (field.textModel !== undefined) {
                const textKey = `${field.name}_text`;
                field.textModel = newData[textKey] || '';
            }
        });

        if (newData.updated_at) {
            updated_at.value = new Date(newData.updated_at).toString();
        }
    }
}, { immediate: true });
</script>

<style scoped>
.nonpathological-section-modern {
    background: #ffffff;
    border-radius: 16px;
    padding: 1.5rem;
}

.section-header-modern {
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f2f5;
}

.header-title-modern {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-title-modern i {
    font-size: 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.header-title-modern h4 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #344767;
}

.add-fields-section {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 8px;
}

.add-field-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    color: #667eea;
    font-size: 0.813rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.add-field-btn i {
    font-size: 0.75rem;
}

.add-field-btn:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.fields-grid-compact {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
}

.field-group-compact {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0.5rem 0;
    transition: all 0.3s ease;
}

.field-group-compact:hover {
    box-shadow: none;
    transform: none;
    background: transparent;
}

.field-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
    padding-bottom: 0;
    border-bottom: none;
}

.form-label-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #344767;
    margin: 0;
}

.label-content-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.label-content-modern i {
    font-size: 0.9375rem;
    color: #667eea;
}

.close-field-btn {
    padding: 0.25rem 0.5rem;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: #9ca3af;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.close-field-btn:hover {
    background: #fee;
    color: #dc2626;
}

.last-updated-wrapper {
    margin-top: 0.5rem;
}

@media (max-width: 768px) {
    .nonpathological-section-modern {
        padding: 1rem;
    }

    .fields-grid-compact {
        grid-template-columns: 1fr;
    }
}
</style>
